import api, { tokenManager } from './api';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    accessToken: string;
    refreshToken: string;
    user: {
      id: number;
      email: string;
      name: string;
      role: 'admin' | 'super_admin' | 'development_admin';
      lastLogin: string;
    };
  };
}

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'super_admin' | 'development_admin';
  isActive: boolean;
  lastLogin: string;
  createdAt: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateProfileRequest {
  name: string;
}

class AuthService {
  // 로그인
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', credentials);

    if (response.data.success) {
      const { accessToken, refreshToken, user } = response.data.data;
      tokenManager.setTokens(accessToken, refreshToken);
      tokenManager.setUser(user);
    }

    return response.data;
  }

  // 로그아웃
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      tokenManager.clearTokens();
    }
  }

  // 현재 사용자 정보 조회
  async getMe(): Promise<User> {
    const response = await api.get<{ success: boolean; data: User }>('/auth/me');
    return response.data.data;
  }

  // 비밀번호 변경
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    await api.put('/auth/me/password', data);
  }

  // 프로필 업데이트
  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await api.put<{ success: boolean; data: User }>('/auth/me/profile', data);

    // 로컬 스토리지의 사용자 정보도 업데이트
    tokenManager.setUser(response.data.data);

    return response.data.data;
  }

  // 토큰 유효성 검증
  async verifyToken(): Promise<User> {
    const response = await api.get<{ success: boolean; data: User }>('/auth/verify');
    return response.data.data;
  }

  // 로그인 상태 확인
  isAuthenticated(): boolean {
    const token = tokenManager.getAccessToken();
    const user = tokenManager.getUser();
    return !!(token && user);
  }

  // 사용자 정보 가져오기 (로컬 스토리지에서)
  getCurrentUser(): User | null {
    return tokenManager.getUser();
  }

  // 권한 확인
  hasRole(role: 'admin' | 'super_admin' | 'development_admin'): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    // development_admin은 모든 권한을 가짐
    if (user.role === 'development_admin') return true;

    if (role === 'admin') {
      return user.role === 'admin' || user.role === 'super_admin';
    }

    return user.role === role;
  }

  // Super Admin 권한 확인 (development_admin 포함)
  isSuperAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'super_admin' || user?.role === 'development_admin';
  }
}

export const authService = new AuthService();
export default authService;